/**
 * Redux slice de « Face à face avec… » (chantier C23 SP6, spec § 7.5) : qui, parmi les notes
 * que je peux lire, a marqué un concept qui répond à l'un des miens, tel que le serveur le rend.
 *
 * Calqué sur `faceAFaceSlice` : indexé par mon concept (`byEntreeId`), garde `requestId`,
 * un 404 est un état (« indisponible »), aucune notification globale, et **chaque lecture repart
 * de rien** : un partage a pu être retiré, une marque validée depuis.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { entreeService } from '../../services/entreeAPI';
import { getTokenRedirect } from '../../utils/authRedirect';
import { tokenRequest } from '../../authConfig';
import type { FaceAFaceAvecDto } from '../../types/api';

export type FaceAFaceAvecStatus = 'idle' | 'loading' | 'ready' | 'indisponible' | 'error';

export interface FaceAFaceAvecEntry {
    status: FaceAFaceAvecStatus;
    data?: FaceAFaceAvecDto;
    requestId: string | null;
}

export interface FaceAFaceAvecState {
    byEntreeId: Record<string, FaceAFaceAvecEntry>;
}

export const initialFaceAFaceAvecState: FaceAFaceAvecState = { byEntreeId: {} };

const EMPTY_ENTRY: FaceAFaceAvecEntry = Object.freeze({ status: 'idle', requestId: null }) as FaceAFaceAvecEntry;

async function tokenOf(instance: any): Promise<string> {
    const accessToken = await getTokenRedirect(tokenRequest, instance);
    if (!accessToken) throw new Error('Impossible d\'obtenir le token d\'accès');
    return accessToken;
}

export const fetchFaceAFaceAvec = createAsyncThunk<
    FaceAFaceAvecDto,
    { entreeId: string; instance: any },
    { rejectValue: 'indisponible' | 'error' }
>('faceAFaceAvec/fetch', async ({ entreeId, instance }, { rejectWithValue }) => {
    try {
        return await entreeService.getFaceAFaceAvec(entreeId, await tokenOf(instance));
    } catch (error: any) {
        return rejectWithValue(error?.response?.status === 404 ? 'indisponible' : 'error');
    }
});

const faceAFaceAvecSlice = createSlice({
    name: 'faceAFaceAvec',
    initialState: initialFaceAFaceAvecState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchFaceAFaceAvec.pending, (state, action) => {
                state.byEntreeId[action.meta.arg.entreeId] = { status: 'loading', requestId: action.meta.requestId };
            })
            .addCase(fetchFaceAFaceAvec.fulfilled, (state, action) => {
                const entry = state.byEntreeId[action.meta.arg.entreeId];
                if (!entry || entry.requestId !== action.meta.requestId) return;
                state.byEntreeId[action.meta.arg.entreeId] = { status: 'ready', data: action.payload, requestId: action.meta.requestId };
            })
            .addCase(fetchFaceAFaceAvec.rejected, (state, action) => {
                const entry = state.byEntreeId[action.meta.arg.entreeId];
                if (!entry || entry.requestId !== action.meta.requestId) return;
                state.byEntreeId[action.meta.arg.entreeId] = { status: action.payload === 'indisponible' ? 'indisponible' : 'error', requestId: action.meta.requestId };
            });
    },
});

export default faceAFaceAvecSlice.reducer;

// --- Sélecteurs (tolérants : un store partiel rend l'entrée vide) ---
export const selectFaceAFaceAvec = (entreeId: string) => (state: any): FaceAFaceAvecEntry =>
    state?.faceAFaceAvec?.byEntreeId?.[entreeId] ?? EMPTY_ENTRY;
